import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { PageResponse } from '../../types';
import { AdminOfferRow, OfferStatus, QUEUE_STATUSES, SUSPENDABLE_STATUSES } from '../../types/offers';
import OfferStatusChip from './OfferStatusChip';
import { discountLabel, formatAed, formatInstant, formatWindow, itemDiscountText, STATUS_LABELS } from './offerReviewModel';

interface OffersQueueTabProps {
  token: string;
  status: OfferStatus;
  onStatusChange: (status: OfferStatus) => void;
  /** Reports the PENDING_APPROVAL total whenever that filter loads, for the tab badge. */
  onPendingCount?: (count: number) => void;
}

const PAGE_SIZE = 20;

function ItemsCell({ row }: { row: AdminOfferRow }) {
  const shown = row.items.slice(0, 3);
  return (
    <ul className="offer-queue__items">
      {shown.map((item) => (
        <li key={item.offeringId}>
          {item.name}{row.discountVaries ? ` (${itemDiscountText(item, row)})` : ''}: <s>{formatAed(item.listPrice)}</s> → <strong>{formatAed(item.discountedPrice)}</strong>
        </li>
      ))}
      {row.items.length > shown.length && <li className="admin-text-muted">+{row.items.length - shown.length} more</li>}
    </ul>
  );
}

/** Spec 062 Flow B / AC-5 — the Offers tab of Service approvals. Keyed by status in the parent, so a new filter starts at page 0. */
export default function OffersQueueTab({ token, status, onStatusChange, onPendingCount }: OffersQueueTabProps) {
  const [page, setPage] = useState(0);
  const [data, setData] = useState<PageResponse<AdminOfferRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.listOfferQueue(token, status, page, PAGE_SIZE)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        if (status === 'PENDING_APPROVAL') onPendingCount?.(res.totalElements);
      })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load offers'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, status, page, onPendingCount]);

  const rows = data?.content ?? [];

  return (
    <div data-testid="offers-tab">
      <div className="admin-toolbar offer-queue__toolbar">
        <label className="admin-form-label" htmlFor="offer-status-filter" style={{ margin: 0 }}>Status</label>
        <select
          id="offer-status-filter"
          className="admin-select"
          value={status}
          onChange={(e) => onStatusChange(e.target.value as OfferStatus)}
        >
          {QUEUE_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
      </div>

      {error && <div className="admin-info-banner danger" role="alert" style={{ marginBottom: '1rem' }}>{error}</div>}

      <div className="admin-card">
        {loading ? (
          <div className="admin-loading">
            <div className="admin-loading-spinner"></div>
          </div>
        ) : rows.length === 0 ? (
          <div className="admin-empty-state">
            <p>{status === 'PENDING_APPROVAL' ? 'No offers waiting' : `No ${STATUS_LABELS[status].toLowerCase()} offers`}</p>
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table offer-queue">
              <thead>
                <tr>
                  <th>Banner</th>
                  <th>Garage</th>
                  <th>Offer</th>
                  <th>Services (before → after)</th>
                  <th>Window</th>
                  <th>Max bookings</th>
                  <th>Arabic</th>
                  <th>Submitted</th>
                  <th>Prior rejections</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} data-testid="offer-row" data-offer-id={row.id}>
                    <td>
                      {row.banner?.downloadUrl
                        ? <img className="offer-queue__thumb" src={row.banner.downloadUrl} alt={`${row.titleEn} banner`} loading="lazy" />
                        : <div className="offer-queue__thumb offer-queue__thumb--empty">No banner</div>}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{row.garageName}</div>
                      <div className="offer-queue__garage-meta">
                        <span className={`admin-badge ${row.garageStatus === 'APPROVED' ? 'approved' : 'warning'}`}>{row.garageStatus}</span>
                        {row.garageBlocked && <span className="admin-badge rejected">Blocked</span>}
                      </div>
                      <div className="admin-text-muted offer-queue__counts">
                        {row.garageLiveCount} running · {row.garagePendingCount} pending
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{row.titleEn}</div>
                      <div className="offer-queue__garage-meta">
                        <span className="offer-queue__label">{discountLabel(row)}</span>
                        <OfferStatusChip status={row.status} />
                        {row.revisionOf && <span className="admin-badge update">Edit of live offer</span>}
                      </div>
                    </td>
                    <td><ItemsCell row={row} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatWindow(row.startsAt, row.endsAt)}</td>
                    <td>{row.maxBookings ?? 'No limit'}</td>
                    <td>
                      {row.hasArabic
                        ? <span className="admin-badge approved">Yes</span>
                        : <span className="admin-badge warning">No</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatInstant(row.submittedAt)}</td>
                    <td>
                      {row.priorRejections > 0
                        ? <span className="admin-badge rejected">{row.priorRejections}</span>
                        : <span className="admin-text-muted">0</span>}
                    </td>
                    <td>
                      <div className="offer-queue__actions">
                        <Link to={`/service-approvals/offers/${row.id}`} className="admin-link">Review</Link>
                        {/* #997: running offers can be edited in place; revisions are decided, not edited. */}
                        {SUSPENDABLE_STATUSES.includes(row.status) && !row.revisionOf && (
                          <Link to={`/service-approvals/offers/${row.id}/edit`} className="admin-link" data-testid="offer-row-edit">Edit</Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && data.totalPages > 1 && (
          <div className="admin-pagination">
            <button
              type="button"
              className="admin-btn admin-btn-outline admin-btn-sm"
              disabled={data.number === 0 || loading}
              onClick={() => setPage(data.number - 1)}
            >
              Previous
            </button>
            <span style={{ fontSize: '0.875rem', color: 'var(--admin-text-muted)' }}>
              Page {data.number + 1} of {data.totalPages}
            </span>
            <button
              type="button"
              className="admin-btn admin-btn-outline admin-btn-sm"
              disabled={data.number + 1 >= data.totalPages || loading}
              onClick={() => setPage(data.number + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
